import { useEffect, useRef, useState } from "react";

/**
 * PC 마우스 드래그 가로 스크롤 + 관성(플링).
 *
 * - pointerType === "mouse" 만 처리 — 터치/펜은 브라우저 네이티브 스크롤
 *   (자체 관성 포함)이 이미 자연스러우므로 건드리지 않는다.
 * - 스냅 없는 트랙 전제: 놓는 순간의 속도로 관성 스크롤을 이어
 *   시간 기반 지수 감쇠(반감기 ≈ 220ms)로 자연 감속시킨다.
 * - 판정은 ref(동기) — state 로 판정하면 같은 프레임의 move/up 이 이전
 *   렌더의 값을 봐서 드래그를 놓친다. isDragging state 는 커서 스타일 전용.
 *
 * 사용: 스크롤 컨테이너에 ref 와 handlers 를 펼치고, isDragging 으로
 * cursor-grab/grabbing 을 토글한다. 자식 이미지에는 draggable={false}.
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragState = useRef({
    dragging: false,
    startX: 0,
    startLeft: 0,
    lastX: 0,
    lastT: 0,
    velocity: 0, // px/ms — 최근 이동으로 지수 평활
  });
  const flingRaf = useRef(0);

  const onPointerDown = (e: React.PointerEvent<T>) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    cancelAnimationFrame(flingRaf.current); // 진행 중 관성 위에서 다시 잡기
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startLeft: el.scrollLeft,
      lastX: e.clientX,
      lastT: e.timeStamp,
      velocity: 0,
    };
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      // 캡처 실패(합성 이벤트 등)해도 드래그 자체는 동작해야 한다
    }
    setIsDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent<T>) => {
    const d = dragState.current;
    const el = ref.current;
    if (!d.dragging || !el) return;
    el.scrollLeft = d.startLeft - (e.clientX - d.startX);
    // 순간 속도(px/ms) — 샘플 노이즈를 지수 평활(0.8)로 눌러 플링 방향 튐 방지
    const dt = e.timeStamp - d.lastT;
    if (dt > 0) {
      const v = (e.clientX - d.lastX) / dt;
      d.velocity = d.velocity * 0.2 + v * 0.8;
      d.lastX = e.clientX;
      d.lastT = e.timeStamp;
    }
  };

  const endDrag = (e: React.PointerEvent<T>) => {
    const d = dragState.current;
    if (!d.dragging) return;
    d.dragging = false;
    try {
      ref.current?.releasePointerCapture(e.pointerId);
    } catch {
      // 캡처되지 않은 포인터 해제는 무시
    }
    setIsDragging(false);

    // 관성 스크롤: 놓는 순간 속도에서 시간 기반 감쇠 — 프레임레이트 무관
    let v = -d.velocity; // 드래그 방향 → 스크롤 방향
    let prev = performance.now();
    const fling = (now: number) => {
      const el = ref.current;
      if (!el) return;
      const dt = now - prev;
      prev = now;
      el.scrollLeft += v * dt;
      v *= Math.exp(-dt / 320);
      const atEdge =
        el.scrollLeft <= 0 ||
        el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if (Math.abs(v) > 0.02 && !atEdge) {
        flingRaf.current = requestAnimationFrame(fling);
      }
    };
    if (Math.abs(v) > 0.1) flingRaf.current = requestAnimationFrame(fling);
  };

  // 언마운트 시 관성 rAF 정리
  useEffect(() => () => cancelAnimationFrame(flingRaf.current), []);

  return {
    ref,
    isDragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
